import { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import API from '../api';

function Files() {
  const [files, setFiles] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedClient, setSelectedClient] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchFiles();
    fetchClients();
  }, []);

  const fetchFiles = async () => {
    try {
      const { data } = await API.get('/files');
      setFiles(data);
    } catch (error) {
      console.error('Error fetching files');
    }
  };

  const fetchClients = async () => {
    try {
      const { data } = await API.get('/clients');
      setClients(data);
    } catch (error) {
      console.error('Error fetching clients');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }
    setUploading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      if (selectedClient) formData.append('client', selectedClient);

      await API.post('/files', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSelectedFile(null);
      setSelectedClient('');
      document.getElementById('fileInput').value = '';
      fetchFiles();
    } catch (err) {
      setError('Upload failed');
    }
    setUploading(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this file?')) {
      try {
        await API.delete(`/files/${id}`);
        fetchFiles();
      } catch (error) {
        console.error('Error deleting file');
      }
    }
  };

  const getFileIcon = (fileType) => {
    if (!fileType) return '📄';
    if (fileType.includes('image')) return '🖼️';
    if (fileType.includes('pdf')) return '📕';
    if (fileType.includes('word') || fileType.includes('document')) return '📝';
    if (fileType.includes('sheet') || fileType.includes('excel')) return '📊';
    if (fileType.includes('zip') || fileType.includes('rar')) return '🗜️';
    return '📄';
  };

  const formatSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <AdminLayout>
      <div style={styles.header}>
        <div>
          <h1 style={styles.heading}>Files</h1>
          <p style={styles.subheading}>Upload and manage client files</p>
        </div>
      </div>

      {/* Upload Section */}
      <div style={styles.uploadCard}>
        <h2 style={styles.uploadTitle}>📤 Upload New File</h2>
        {error && <div style={styles.error}>{error}</div>}
        <div style={styles.uploadRow}>
          <input
            id="fileInput"
            type="file"
            style={styles.fileInput}
            onChange={(e) => setSelectedFile(e.target.files[0])}
          />
          <select
            style={styles.select}
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
          >
            <option value="">Assign to client (optional)</option>
            {clients.map((c) => (
              <option key={c._id} value={c._id}>{c.name}</option>
            ))}
          </select>
          <button
            style={styles.uploadBtn}
            onClick={handleUpload}
            disabled={uploading}
          >
            {uploading ? 'Uploading...' : '⬆️ Upload'}
          </button>
        </div>
        {selectedFile && (
          <p style={styles.selectedFile}>
            Selected: {selectedFile.name} ({formatSize(selectedFile.size)})
          </p>
        )}
      </div>

      {/* Files Table */}
      <div style={styles.tableContainer}>
        {files.length === 0 ? (
          <div style={styles.empty}>No files uploaded yet.</div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableHeader}>
                <th style={styles.th}>File</th>
                <th style={styles.th}>Size</th>
                <th style={styles.th}>Client</th>
                <th style={styles.th}>Uploaded</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {files.map((file) => (
                <tr key={file._id} style={styles.tableRow}>
                  <td style={styles.td}>
                    <span style={styles.fileIcon}>
                      {getFileIcon(file.fileType)}
                    </span>
                    {file.originalName}
                  </td>
                  <td style={styles.td}>{formatSize(file.fileSize)}</td>
                  <td style={styles.td}>
                    {file.client?.name || '—'}
                  </td>
                  <td style={styles.td}>
                    {new Date(file.createdAt).toLocaleDateString()}
                  </td>
                  <td style={styles.td}>
                  <a  
                  href={`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'}/uploads/${file.filename}`}
                      target="_blank"
                      rel="noreferrer"
                      style={styles.downloadBtn}
                    >
                      ⬇️ Download
                    </a>
                    <button
                      style={styles.deleteBtn}
                      onClick={() => handleDelete(file._id)}
                    >
                      🗑️ Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AdminLayout>
  );
}

const styles = {
  header: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: '30px'
  },
  heading: { margin: '0', fontSize: '28px', fontWeight: '700', color: '#1a1a2e' },
  subheading: { margin: '4px 0 0 0', color: '#718096', fontSize: '15px' },
  uploadCard: {
    backgroundColor: 'white', borderRadius: '12px', padding: '24px',
    marginBottom: '24px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)'
  },
  uploadTitle: { margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600', color: '#1a1a2e' },
  error: {
    backgroundColor: '#fee2e2', color: '#dc2626', padding: '10px',
    borderRadius: '8px', marginBottom: '16px', fontSize: '14px'
  },
  uploadRow: { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' },
  fileInput: {
    flex: 2, padding: '10px', border: '1px dashed #ddd',
    borderRadius: '8px', fontSize: '14px', cursor: 'pointer'
  },
  select: {
    flex: 1, padding: '10px 12px', border: '1px solid #ddd',
    borderRadius: '8px', fontSize: '14px', minWidth: '180px'
  },
  uploadBtn: {
    padding: '10px 24px', backgroundColor: '#4f46e5', color: 'white',
    border: 'none', borderRadius: '8px', cursor: 'pointer',
    fontSize: '14px', fontWeight: '600'
  },
  selectedFile: { margin: '12px 0 0 0', fontSize: '13px', color: '#718096' },
  tableContainer: {
    backgroundColor: 'white', borderRadius: '12px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.06)', overflow: 'hidden'
  },
  empty: { padding: '60px', textAlign: 'center', color: '#718096' },
  table: { width: '100%', borderCollapse: 'collapse' },
  tableHeader: { backgroundColor: '#f7f8fc' },
  th: {
    padding: '14px 20px', textAlign: 'left', fontSize: '13px',
    fontWeight: '600', color: '#718096', borderBottom: '1px solid #e2e8f0'
  },
  tableRow: { borderBottom: '1px solid #f0f0f0' },
  td: { padding: '14px 20px', fontSize: '14px', color: '#4a5568', verticalAlign: 'middle' },
  fileIcon: { marginRight: '8px', fontSize: '18px' },
  downloadBtn: {
    padding: '6px 12px', backgroundColor: '#dbeafe', color: '#1d4ed8',
    borderRadius: '6px', fontSize: '13px', textDecoration: 'none',
    marginRight: '8px'
  },
  deleteBtn: {
    padding: '6px 12px', backgroundColor: '#fee2e2', color: '#dc2626',
    border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px'
  }
};

export default Files;